import React, { useState, useEffect } from 'react';
import { Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { enqueueSnackbar } from 'notistack';
import { ExportHubAPI, ExportProduct } from 'api/ExportHubAPI';
import StyledSingleSelect from './StyledSingleSelect';
import { USML_CATEGORIES } from './jurisdictionFields';
import { JurisdictionPill } from './productPresets';

interface Props {
  open: boolean;
  databaseId: string;
  licenseId: string;
  groupId: string | undefined;
  linkedIds: string[];
  onClose: () => void;
  onLinked: () => void;
}

const LinkProductModal: React.FC<Props> = ({
  open,
  databaseId,
  licenseId,
  groupId,
  linkedIds,
  onClose,
  onLinked,
}) => {
  const [products, setProducts] = useState<ExportProduct[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  // Optional category applied to the whole batch — the "add several parts
  // under Category VIII" case. Blank lets each product bring its own.
  const [batchCategory, setBatchCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !groupId) return;
    setSelected([]);
    setLoading(true);
    ExportHubAPI.getProducts(databaseId, groupId, { limit: 200 })
      .then((res) => setProducts(res.products))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [open, databaseId, groupId]);

  const toggle = (id: string) =>
    setSelected((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : [...s, id],
    );

  const handleLink = async () => {
    if (!groupId || selected.length === 0) return;
    setSaving(true);
    try {
      await ExportHubAPI.linkProducts(licenseId, {
        group_id: groupId,
        product_ids: selected,
        usml_category: batchCategory.trim() || undefined,
      });
      enqueueSnackbar(
        `Linked ${selected.length} product${selected.length === 1 ? '' : 's'}.`,
        { variant: 'success' },
      );
      onLinked();
    } catch (err) {
      console.error('Failed to link products:', err);
      enqueueSnackbar('Failed to link products.', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const linkedSet = new Set(linkedIds);

  return (
    <Modal open={open} onClose={onClose} className='eh-modal-root'>
      <div className='eh-modal' role='dialog' aria-label='Link Product'>
        <div className='eh-modal-head'>
          <h2 className='eh-modal-title'>Link Product</h2>
          <button
            className='eh-modal-close'
            onClick={onClose}
            aria-label='Close'
            type='button'
          >
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        <div className='eh-modal-body'>
          <div className='eh-field'>
            <span className='eh-field-label'>
              USML Category for these parts
            </span>
            <StyledSingleSelect
              options={[
                { value: '', label: 'Use each product’s own category' },
                ...USML_CATEGORIES.map((c) => ({ value: c, label: c })),
              ]}
              value={batchCategory}
              onChange={setBatchCategory}
              inline
            />
            <span className='eh-field-hint'>
              Files every part in this batch under one category (DSP-5 Block
              11). Each line stays editable afterwards.
            </span>
          </div>

          {loading && <div className='eh-detail-note'>Loading products…</div>}
          {!loading && products.length === 0 && (
            <div className='eh-detail-note'>
              No products in this database yet.
            </div>
          )}
          <div className='eh-link-list'>
            {products.map((p) => {
              const already = linkedSet.has(p.id);
              return (
                <button
                  key={p.id}
                  type='button'
                  className={`eh-link-row ${
                    selected.includes(p.id) ? 'sel' : ''
                  } ${already ? 'disabled' : ''}`}
                  onClick={() => !already && toggle(p.id)}
                  disabled={already}
                >
                  <span
                    className={`eh-facet-check ${
                      already || selected.includes(p.id) ? 'on' : ''
                    }`}
                  >
                    {(already || selected.includes(p.id)) && '✓'}
                  </span>
                  <span className='eh-link-main'>
                    <span className='eh-link-title'>
                      {p.name}
                      <span className='eh-link-sub'>
                        {p.internal_sku || 'No SKU'}
                      </span>
                    </span>
                    <span className='eh-link-meta'>
                      <JurisdictionPill value={p.jurisdiction} />
                    </span>
                  </span>
                  {already && <span className='eh-link-tag'>Linked</span>}
                </button>
              );
            })}
          </div>
        </div>

        <div className='eh-modal-foot'>
          <button
            className='btn-outline'
            type='button'
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className='btn-primary'
            type='button'
            onClick={handleLink}
            disabled={saving || selected.length === 0}
            style={{ opacity: saving || selected.length === 0 ? 0.6 : 1 }}
          >
            {saving ? 'Linking…' : `Link ${selected.length || ''}`.trim()}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default LinkProductModal;
